export interface ICreateStoreMutationResult {
  data?: unknown;
  error?: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const hasSuccessfulStatus = (payload: Record<string, unknown>): boolean => {
  if (payload.status == null) {
    return false;
  }

  return String(payload.status).toLowerCase() === 'success';
};

export const resolveCreateStoreId = (data: Record<string, unknown> | undefined): number | null => {
  if (!data) {
    return null;
  }

  const candidateIds = [data.id, data.order_id, data.store_id, data.orderId, data.storeId];
  const matchedCandidate = candidateIds.find((candidate) => {
    const numericId = Number(candidate);
    return Number.isFinite(numericId) && numericId > 0;
  });

  return matchedCandidate != null ? Number(matchedCandidate) : null;
};

const parseWrappedCreateStoreBody = (body: unknown): number | null => {
  if (!isRecord(body) || !hasSuccessfulStatus(body)) {
    return null;
  }

  const nestedData = body.data;

  if (!isRecord(nestedData)) {
    return null;
  }

  return (
    resolveCreateStoreId(nestedData) ??
    resolveCreateStoreId(isRecord(nestedData.store) ? nestedData.store : undefined)
  );
};

export const parseCreateStorePayload = (body: unknown): number | null => {
  if (!isRecord(body)) {
    return null;
  }

  // RTK Query treats `{ status, data }` baseQuery results as `{ data: innerData }`.
  const directId = resolveCreateStoreId(body);

  if (directId != null) {
    return directId;
  }

  return parseWrappedCreateStoreBody(body);
};

export const parseCreateStoreResponseStoreId = (body: unknown): number | null =>
  parseCreateStorePayload(body);

const getMutationErrorData = (error: unknown): unknown => {
  if (!isRecord(error)) {
    return undefined;
  }

  return error.data;
};

/** RTK may surface a 2xx body on `data` or a non-2xx success payload on `error.data`. */
export const parseCreateStoreMutationStoreId = (
  result: ICreateStoreMutationResult
): number | null =>
  parseCreateStorePayload(result.data) ??
  parseCreateStorePayload(getMutationErrorData(result.error)) ??
  null;
